import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  Youtube,
  Mail,
  Phone,
  MapPin,
  Send
} from 'lucide-react';

const LOGO_URL =
  'https://customer-assets.emergentagent.com/job_agency-square/artifacts/7zqhhxkv_squre%20trigger%20black%20%282%29.png';

const serviceLinks = [
  { name: 'SEO Services', path: '/services/seo-services' },
  { name: 'UI / UX Design', path: '/services/ui-ux-design' },
  { name: 'Website Development', path: '/services/website-development' },
  { name: 'Custom Tool Creation', path: '/services/custom-tool-creation' },
  { name: 'Social Media Marketing', path: '/services/social-media-marketing' },
  { name: 'E-Commerce Solutions', path: '/services/e-commerce-solutions' }
];

const companyLinks = [
  { name: 'Contact', path: '/contact' },
  { name: 'About Us', path: '/company/about' }

  // { name: 'Our Process', path: '/company/process' },
  // { name: 'Our Values', path: '/company/values' },
  // { name: 'Careers', path: '/company/careers' }
];

const resourceLinks = [
  { name: 'Blog', path: '/blog' },
  { name: 'FAQs', path: '/resources/faqs' }

  // { name: 'Case Studies', path: '/portfolio/case-studies' },
  // { name: 'Free Resources', path: '/resources/free' },
  // { name: 'News / Press', path: '/resources/news' },
  // { name: 'Client Testimonials', path: '/portfolio/testimonials' }
];

const socialLinks = [
  { name: 'Facebook', icon: Facebook, url: 'https://www.facebook.com/profile.php?id=61585572042270' },
  { name: 'Twitter', icon: Twitter, url: '' },
  { name: 'Instagram', icon: Instagram, url: 'https://www.instagram.com/square_trigger.official/' },
  { name: 'LinkedIn', icon: Linkedin, url: 'https://www.linkedin.com/company/square-trigger/' },
  { name: 'YouTube', icon: Youtube, url: '#' }
];

export const Footer = () => {
  const [email, setEmail] = React.useState('');
  const navigate = useNavigate();

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    alert('Thank you for subscribing!');
    setEmail('');
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleInternalNavigation = (e, path) => {
    e.preventDefault();
    navigate(path);
    scrollToTop();
  };

  return (
    <footer className="bg-foreground text-background relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="absolute top-0 left-0 w-72 h-72 md:w-96 md:h-96 rounded-full bg-primary blur-3xl" />
        <div className="absolute bottom-0 right-0 w-72 h-72 md:w-96 md:h-96 rounded-full bg-secondary blur-3xl" />
      </div>

      <div className="container-custom relative z-20 pointer-events-auto">
        {/* Newsletter Section */}
        <div className="border-b border-background/10 py-12 md:py-16">
          <div className="max-w-2xl mx-auto text-center">
            <motion.h3
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-2xl md:text-3xl font-display font-bold mb-4"
            >
              Stay Ahead with Digital Insights
            </motion.h3>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-background/70 mb-8 px-2"
            >
              Subscribe to our newsletter for the latest trends, tips, and insights in digital
              marketing and web development.
            </motion.p>

            <motion.form
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              onSubmit={handleNewsletterSubmit}
              className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
            >
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-background/10 border-background/20 text-background placeholder:text-background/50 focus:border-primary h-11"
                required
              />
              <Button variant="gradient" type="submit" className="shrink-0 h-11">
                Subscribe
                <Send className="h-4 w-4 ml-2" />
              </Button>
            </motion.form>
          </div>
        </div>

        {/* Main Footer */}
        <div className="py-12 md:py-16 grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16 lg:gap-20 items-start justify-between">
          {/* 1st Column - Brand */}
          <div className="flex flex-col items-center md:items-start text-center md:text-left">
            <Link
              to="/"
              onClick={(e) => handleInternalNavigation(e, '/')}
              className="inline-block mb-6 relative z-20"
            >
              <img
                src={LOGO_URL}
                alt="Square Trigger"
                className="h-12 md:h-14 w-auto object-contain brightness-0 invert mx-auto md:mx-0"
              />
            </Link>

            <p className="text-background/70 mb-6 max-w-sm leading-relaxed">
              Your Digital Success Partner. We help businesses transform their digital presence
              with innovative solutions, strategic marketing, and cutting-edge technology.
            </p>

            {/* Contact Info */}
            <div className="space-y-4 w-full flex flex-col items-center md:items-start">
              <a
                href="mailto:info@squaretrigger.com"
                className="relative z-20 w-full flex justify-center md:justify-start text-background/70 hover:text-background transition-colors"
              >
                <span className="inline-flex items-center gap-3 max-w-fit text-center md:text-left">
                  <Mail className="h-4 w-4 shrink-0" />
                  <span className="break-all">info@squaretrigger.com</span>
                </span>
              </a>

              <a
                href="tel:+12368693335"
                className="relative z-20 w-full flex justify-center md:justify-start text-background/70 hover:text-background transition-colors"
              >
                <span className="inline-flex items-center gap-3 max-w-fit text-center md:text-left">
                  <Phone className="h-4 w-4 shrink-0" />
                  <span>+1 (236) 869-3335</span>
                </span>
              </a>

              <div className="w-full flex justify-center md:justify-start text-background/70">
                <div className="inline-flex items-start gap-3 max-w-[290px] md:max-w-sm text-left">
                  <MapPin className="h-4 w-4 mt-1 shrink-0" />
                  <span className="leading-relaxed">
                    1655 Haro Street, Vancouver, B.C (V6G1G9)
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* 2nd Column - Services */}
          <div className="flex flex-col items-center text-center">
            <h4 className="font-display font-semibold text-xl mb-6 text-background">
              Services
            </h4>

            <ul className="space-y-4">
              {serviceLinks.map((link) => (
                <li key={link.name} className="flex justify-center">
                  <Link
                    to={link.path}
                    onClick={(e) => handleInternalNavigation(e, link.path)}
                    className="relative z-20 text-background/70 hover:text-background transition-colors inline-block py-1 px-2 cursor-pointer"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* 3rd Column - Company + Resources */}
          <div className="flex flex-col items-center text-center">
            <h4 className="font-display font-semibold text-xl mb-6 text-background">
              Company
            </h4>

            <ul className="space-y-4 mb-10">
              {companyLinks.map((link) => (
                <li key={link.name} className="flex justify-center">
                  <Link
                    to={link.path}
                    onClick={(e) => handleInternalNavigation(e, link.path)}
                    className="relative z-20 text-background/70 hover:text-background transition-colors inline-block py-1 px-2 cursor-pointer"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>

            <h4 className="font-display font-semibold text-xl mb-6 text-background">
              Resources
            </h4>

            <ul className="space-y-4">
              {resourceLinks.map((link) => (
                <li key={link.name} className="flex justify-center">
                  <Link
                    to={link.path}
                    onClick={(e) => handleInternalNavigation(e, link.path)}
                    className="relative z-20 text-background/70 hover:text-background transition-colors inline-block py-1 px-2 cursor-pointer"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-background/10 py-8">
          <div className="flex flex-col items-center justify-center md:flex-row md:justify-between gap-6 text-center">
            <div className="text-background/60 text-sm">
              © {new Date().getFullYear()} Square Trigger. All rights reserved.
            </div>

            {/* Social Links */}
            <div className="flex items-center justify-center gap-3 flex-wrap">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <motion.a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative z-20 p-3 rounded-lg bg-background/10 text-background/70 hover:bg-primary hover:text-primary-foreground transition-all duration-300"
                    whileHover={{ scale: 1.08 }}
                    whileTap={{ scale: 0.95 }}
                    aria-label={social.name}
                  >
                    <Icon className="h-4 w-4" />
                  </motion.a>
                );
              })}
            </div>

            {/* Legal Links */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 text-sm">
              <Link
                to="/privacy"
                onClick={(e) => handleInternalNavigation(e, '/privacy')}
                className="relative z-20 text-background/60 hover:text-background transition-colors cursor-pointer"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms"
                onClick={(e) => handleInternalNavigation(e, '/terms')}
                className="relative z-20 text-background/60 hover:text-background transition-colors cursor-pointer"
              >
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Schema JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'Square Trigger',
          description:
            'Your Digital Success Partner - Web Development, SEO, Social Media Marketing, and Digital Solutions',
          url: 'https://squaretrigger.com',
          logo: LOGO_URL,
          contactPoint: {
            '@type': 'ContactPoint',
            telephone: '+1-236-869-3335',
            contactType: 'customer service',
            email: 'info@squaretrigger.com'
          },
          sameAs: [
            'https://facebook.com/squaretrigger',
            'https://twitter.com/squaretrigger',
            'https://instagram.com/squaretrigger',
            'https://linkedin.com/company/squaretrigger'
          ]
        })}
      </script>
    </footer>
  );
};